import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Simple, bulletproof Google Analytics implementation
 * This component ensures GA scripts load and track properly
 */
const SimpleAnalytics = () => {
  const location = useLocation();

  // Get environment variables
  const GA_ID = import.meta.env.VITE_GA_ID;
  const ANALYTICS_ENABLED = import.meta.env.VITE_ENABLE_ANALYTICS === "true";
  const DEBUG_MODE =
    import.meta.env.VITE_DEBUG_MODE === "true" || import.meta.env.DEV;

  useEffect(() => {
    // Only proceed if we have a GA ID and analytics is enabled
    if (!GA_ID || !ANALYTICS_ENABLED) {
      if (DEBUG_MODE) {
        console.log("🚫 Google Analytics not loaded:", {
          GA_ID: GA_ID || "Missing",
          ANALYTICS_ENABLED,
          reason: !GA_ID ? "No GA_ID provided" : "Analytics disabled",
        });
      }
      return;
    }

    // Load Google Analytics script
    loadGoogleAnalytics();
  }, [GA_ID, ANALYTICS_ENABLED, DEBUG_MODE]);

  // Track page views on route changes
  useEffect(() => {
    if (GA_ID && ANALYTICS_ENABLED && window.gtag) {
      trackPageView();
    }
  }, [location.pathname, GA_ID, ANALYTICS_ENABLED]);

  const loadGoogleAnalytics = () => {
    // Check if already loaded
    if (document.querySelector(`script[src*="gtag/js?id=${GA_ID}"]`)) {
      if (DEBUG_MODE) {
        console.log("✅ Google Analytics script already loaded");
      }
      return;
    }

    if (DEBUG_MODE) {
      console.log("🚀 Loading Google Analytics...", { GA_ID });
    }

    // Create and load the gtag script
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
    script.onload = () => {
      initializeGoogleAnalytics();
    };
    script.onerror = (error) => {
      console.error("❌ Failed to load Google Analytics script:", error);
    };

    document.head.appendChild(script);

    // Initialize dataLayer immediately
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () {
      window.dataLayer.push(arguments);
    };
  };

  const initializeGoogleAnalytics = () => {
    if (DEBUG_MODE) {
      console.log("🔧 Initializing Google Analytics...", { GA_ID });
    }

    // Initialize gtag
    window.gtag("js", new Date());

    // Configure Google Analytics
    window.gtag("config", GA_ID, {
      page_title: document.title,
      page_location: window.location.href,
      page_path: window.location.pathname,
      send_page_view: true,
      debug_mode: DEBUG_MODE,
    });

    if (DEBUG_MODE) {
      console.log("✅ Google Analytics initialized successfully");

      // Test the setup
      setTimeout(() => {
        testAnalyticsSetup();
      }, 1000);
    }
  };

  const trackPageView = () => {
    if (!window.gtag) {
      if (DEBUG_MODE) {
        console.warn("⚠️ gtag not available for page view tracking");
      }
      return;
    }

    const pageData = {
      page_title: document.title,
      page_location: window.location.href,
      page_path: location.pathname,
      content_group1: getContentGroup(location.pathname),
    };

    window.gtag("event", "page_view", pageData);

    if (DEBUG_MODE) {
      console.log("📊 Page view tracked:", pageData);
    }
  };

  const getContentGroup = (pathname) => {
    if (pathname.startsWith("/syllabus")) return "Syllabus";
    if (pathname.startsWith("/reading-material")) return "Reading Materials";
    if (pathname.startsWith("/mcqs")) return "MCQs";

    if (pathname === "/") return "Home";
    return "Other";
  };

  const testAnalyticsSetup = () => {
    if (!window.gtag) {
      console.error("❌ gtag function not available after initialization");
      return;
    }

    console.group("🧪 Testing Analytics Setup");

    // Test custom event
    window.gtag("event", "analytics_test", {
      event_category: "system",
      event_label: "setup_verification",
      value: 1,
      debug: true,
    });

    console.log("✅ Test event sent");
    console.log("📈 Check Google Analytics Real-Time reports");
    console.log("🔍 You should see activity within 30 seconds");
    console.groupEnd();

    // Make test function globally available
    window.testAnalytics = () => {
      window.gtag("event", "manual_test", {
        event_category: "debug",
        event_label: "manual_trigger",
        value: Date.now(),
        debug: true,
      });
      console.log("🚀 Manual test event sent to Google Analytics");
    };

    console.log(
      "💡 You can now run window.testAnalytics() to send test events",
    );
  };

  // This component doesn't render anything visible
  return null;
};

export default SimpleAnalytics;
